import React, { useState, useEffect, useRef } from 'react';
import '../styles/pages/Making.scss';
import ArrowButton from '../components/utils/button/ArrowButton';
import { useNavigate, useLocation } from 'react-router-dom';

import FinalReadingModal from '../components/reading/FinalReadingModal';
import LandScapeModal from '../components/utils/modal/LandScapeModal';
import { logStorylandReading, generateTTS } from '../api/LibraryAPI';
import ErrorModal from '../components/utils/modal/ErrorModal';
import { ERROR } from '../utils/Constants';
import StoryImage from '../components/utils/StoryImage';

const Reading = () => {
    const USER_ID = localStorage.getItem('userID');

    const location = useLocation();
    const { bookList, pageName, bookID } = location.state;
    const story = bookList.contents_json.story;

    const [index, setIndex] = useState(-1);
    const [showFinalReadingModal, setShowFinalReadingModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [bgImage, setBgImage] = useState('');
    const [contentText, setContentText] = useState([]);
    const [speechState, setSpeechState] = useState(false);
    const [audioCache, setAudioCache] = useState({});
    const [imageSrc, setImageSrc] = useState('/images/utils/button/music-on.svg');
    const [currentAudioIndex, setCurrentAudioIndex] = useState(null);

    const audioRef = useRef(new Audio());
    const fetchingRef = useRef({});

    const navigate = useNavigate();

    useEffect(() => {
        if (pageName === 'storyland') {
            logStorylandReading(USER_ID, bookID).then((res) => {
                if (!res.success) {
                    setErrorMessage(ERROR.lack);
                    setShowErrorModal(true);
                }
            });
        }
    }, []);

    const fetchTTS = async (pageIndex) => {
        if (audioCache[pageIndex] || fetchingRef.current[pageIndex]) return;

        fetchingRef.current[pageIndex] = true;
        let textContent = pageIndex === -1 ? bookList.title : story[pageIndex]?.page_content || '';

        try {
            const response = await generateTTS(USER_ID, textContent, 'vdain', 0);
            if (response.success) {
                const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
                const audioURL = URL.createObjectURL(audioBlob);
                setAudioCache((prev) => ({ ...prev, [pageIndex]: audioURL }));
            } else {
                console.error('TTS API 오류');
            }
        } catch (error) {
            console.error('TTS 요청 오류:', error);
        } finally {
            fetchingRef.current[pageIndex] = false;
        }
    };

    const handleButtonClick = (direction) => {
        setIndex((prevIndex) => {
            let newIndex = prevIndex + (direction === 'left' ? -1 : 1);
            if (newIndex < -1) {
                newIndex = story.length - 1;
            } else if (newIndex >= story.length) {
                setShowFinalReadingModal(true);
                return prevIndex;
            }
            return newIndex;
        });
    };

    const handleSpeechState = () => {
        if (!speechState) {
            setImageSrc('/images/utils/button/music-off.svg');
            if (!audioCache[index]) fetchTTS(index);
        } else {
            audioRef.current.pause();
            setImageSrc('/images/utils/button/music-on.svg');
        }
        setSpeechState((prev) => !prev);
    };

    useEffect(() => {
        if (index === -1) {
            setBgImage(bookList.bookcover);
            setContentText(bookList.title);
            setShowFinalReadingModal(false);
        } else {
            setBgImage(story[index]?.background || '');
            let contents = story[index]?.page_content || '';
            if (contents.includes('\\')) contents = contents.replaceAll('\\', '');
            const sentences = contents.split('. ').map((s) => s.trim() + '.');
            setContentText(sentences);
        }

        if (speechState) {
            if (!audioCache[index]) fetchTTS(index);
            if (index + 1 < story.length && !audioCache[index + 1]) fetchTTS(index + 1);
        }
    }, [index]);

    useEffect(() => {
        if (speechState && audioCache[index] && index !== currentAudioIndex) {
            audioRef.current.pause();
            audioRef.current.src = audioCache[index];
            audioRef.current
                .play()
                .then(() => {
                    setCurrentAudioIndex(index);
                })
                .catch((error) => console.error('Audio play error:', error));
        }
    }, [audioCache, index, speechState]);

    const handleAudioEnd = () => {
        setImageSrc('/images/utils/button/music-off.svg');
    };

    const handleExit = () => {
        setAudioCache({});
        setShowFinalReadingModal(false);
    };

    return (
        <>
            <section className="stories-section">
                {showErrorModal && (
                    <ErrorModal
                        error_message={errorMessage}
                        onClose={() => {
                            setShowErrorModal(false);
                            navigate('/storyland');
                        }}
                    />
                )}
                <StoryImage src={bgImage} alt="bg-img" />
                <ArrowButton
                    onClick={() => handleButtonClick('left')}
                    direction="left"
                    className="story-btn prev-btn"
                />
                <ArrowButton
                    onClick={() => handleButtonClick('right')}
                    direction="right"
                    className="story-btn next-btn"
                />
                <div
                    className={
                        typeof contentText === 'string'
                            ? 'story-contents-container title'
                            : 'story-contents-container'
                    }
                >
                    <ul
                        className={
                            typeof contentText === 'string'
                                ? 'story-contents title'
                                : 'story-contents'
                        }
                    >
                        {typeof contentText === 'string' ? (
                            <>
                                {contentText}
                                <div className="text-stroke">{contentText}</div>
                            </>
                        ) : (
                            <>
                                {contentText.map((sentence, i) => (
                                    <li key={i} className="origin-text">
                                        {sentence.trim()}
                                        <div className="text-stroke">{sentence.trim()}</div>
                                    </li>
                                ))}
                            </>
                        )}
                    </ul>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSpeechState();
                    }}
                    className="text-music-btn"
                >
                    <img src={imageSrc} alt="음성 듣기 버튼" />
                </button>
                <audio ref={audioRef} onEnded={handleAudioEnd} />
                <div className="numbering">{index + 1}</div>
            </section>
            {showFinalReadingModal && (
                <FinalReadingModal
                    onBack={() => setIndex(-1)}
                    pageName={pageName}
                    onClose={handleExit}
                />
            )}
            <LandScapeModal />
        </>
    );
};

export default Reading;
